/* The catalogue of lessons, on paper.

   One sheet is one printed page, so a group longer than a page becomes several
   balanced sheets rather than one that breaks wherever the page ends.

   Split by strand, not by school type: the year alone no longer says which one
   it is — the lower stage of a primary school and the middle of a secondary one
   both have a third year — so the heading has to.

   It used to split by shape as well: the five planning steps on one set of
   sheets, the applications on another. Item 12 of PLAN.md converted every
   example into a lesson, and a lesson has no shape — so that split had nothing
   left to divide. Kept as it was, it would have put all fifty-nine lessons onto
   sheets headed "Aplikace" with not one application among them: a printed sheet
   lying about its own contents, and paper cannot be corrected after the fact.

   OWNED BY ITEM 9 OF docs/plan/00-prestavba-2026-08.md, split removed by ITEM 12
   OF PLAN.md. */

import type { GeneratedSheet, Pick } from "./handout";
import type { Block, IndexEntry, Localized, Taxonomy } from "./types";

/* Raise or lower this if the row height changes. To check the split still
   works, set it to 5 and rebuild: the long groups become three balanced sheets,
   each with its own heading and table header. */
export const OVERVIEW_ROWS = 14;

/* The title repeats the subject and the year, which the table has columns for. */
function topic(title: string): string {
  const dash = title.indexOf("—");
  const rest = dash >= 0 ? title.slice(dash + 1) : title;
  return rest.replace(/,?\s*\d+\.\s*ročník\s*$/, "").trim();
}

export function exampleSheets(
  index: IndexEntry[], taxonomy: Taxonomy, pick: Pick,
  t: (v: Localized | undefined) => string,
): GeneratedSheet[] {
  const strand = pick.strand ?? "";
  const rows = index
    .filter((e) => e.strand.includes(strand))
    .sort((a, b) => a.year - b.year
      || t(taxonomy.subjects[a.subject]?.name).localeCompare(
         t(taxonomy.subjects[b.subject]?.name), "cs"));

  if (!rows.length) return [];

  const name = t(taxonomy.strands[strand]?.name) || strand;
  const heading = `Lekce — ${name}`;
  const count = Math.max(1, Math.ceil(rows.length / OVERVIEW_ROWS));
  const per = Math.ceil(rows.length / count);

  return Array.from({ length: count }, (_, i) => rows.slice(i * per, (i + 1) * per))
    .filter((part) => part.length)
    .map((part, i) => ({
      title: heading + (count > 1 ? ` (${i + 1}/${count})` : ""),
      blocks: [{
        k: "table",
        head: ["Předmět", "Ročník", "Téma"],
        rows: part.map((e) => [
          t(taxonomy.subjects[e.subject]?.name) || e.subject,
          `${e.year}.`,
          topic(t(e.title)),
        ]),
      } as Block],
    }));
}
